import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStore } from "@/store/AppStore";
import { entryOf, absenceOn, timeOf, firstStart, worked, paused, fmtMin, ABSENCE, SHIFT_TOLERANCE } from "@/lib/attendance";
import { TODAY } from "@/data/mock";

const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
export const monthLabel = (key) => `${MONTHS[Number(key.slice(5)) - 1]} ${key.slice(0, 4)}`;

const buildDays = (state, collab, month) => {
  const [y, m] = month.split("-").map(Number);
  return Array.from({ length: new Date(y, m, 0).getDate() }, (_, i) => {
    const date = `${month}-${String(i + 1).padStart(2, "0")}`;
    const entry = entryOf(state, collab.id, date);
    const abs = absenceOn(state, collab.id, date);
    const wd = new Date(`${date}T12:00:00`).getDay();
    const start = firstStart(entry);
    const note = abs ? ABSENCE[abs.type].label : start && start > SHIFT_TOLERANCE ? "Atraso" : !entry && wd === 0 ? "Domingo" : !entry && date < TODAY ? "Sem registro" : "";
    return { date, day: i + 1, wd, entry, abs, start, note, future: date > TODAY };
  });
};

const Cell = ({ children, className = "" }) => <td className={`border border-neutral-200 px-2 py-1 ${className}`}>{children}</td>;

function Totals({ days }) {
  const n = (t) => days.filter((d) => d.abs?.type === t).length;
  const items = [["Dias trabalhados", days.filter((d) => d.entry?.events.length).length], ["Horas trabalhadas", fmtMin(days.reduce((a, d) => a + worked(d.entry), 0))], ["Atrasos", days.filter((d) => d.note === "Atraso").length], ["Faltas", n("falta")], ["Férias", n("ferias")], ["Atestados", n("atestado")], ["Folgas", n("folga")]];
  return (
    <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="timesheet-totals">
      {items.map(([l, v]) => <div key={l} className="rounded-lg border border-neutral-200 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-neutral-500">{l}</p><p className="font-display text-lg font-semibold">{v}</p></div>)}
    </div>
  );
}

const Sign = ({ label, name }) => (
  <div className="w-56 text-center"><div className="border-t border-neutral-500 pt-2 text-xs font-semibold">{name}</div><p className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</p></div>
);

export default function TimesheetPage() {
  const { state, user } = useStore();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  if (user === false) return <Navigate to="/" replace />;
  if (!state) return null;
  const collab = user.role !== "colaboradora" && state.collaborators.find((c) => c.id === params.get("colab"));
  const company = collab && state.companies.find((c) => c.id === collab.companyId);
  const month = /^\d{4}-\d{2}$/.test(params.get("mes") || "") ? params.get("mes") : TODAY.slice(0, 7);
  const days = collab ? buildDays(state, collab, month) : [];
  return (
    <div className="min-h-screen bg-neutral-100 px-4 py-8 print:bg-white print:p-0" data-testid="timesheet-page">
      <style>{"@page { size: A4 portrait; margin: 10mm } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact } }"}</style>
      <div className="mx-auto mb-6 flex max-w-4xl justify-between print:hidden">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="timesheet-back"><ArrowLeft className="mr-1 h-4 w-4" /> Voltar</Button>
        {collab && <Button className="font-semibold" onClick={() => window.print()} data-testid="timesheet-print"><Printer className="mr-1.5 h-4 w-4" /> Imprimir / salvar PDF</Button>}
      </div>
      {!collab ? <p className="text-center text-sm text-neutral-500" data-testid="timesheet-unavailable">Espelho de ponto não disponível.</p> : (
        <div className="mx-auto max-w-4xl bg-white p-6 shadow-xl sm:p-10 print:max-w-none print:p-0 print:shadow-none" data-testid="timesheet">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-gold pb-4">
            <div className="flex items-center gap-4">
              <img src="/img/logo.jpg" alt="Empreendi Bellas" className="h-14 w-14 rounded-lg object-cover" />
              <div><p className="font-display text-xl font-semibold">{company.name}</p><p className="text-xs text-neutral-500">{company.city}</p></div>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold-dark">Espelho de ponto</p>
              <p className="font-display text-lg font-semibold" data-testid="timesheet-month">{monthLabel(month)}</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <p><span className="text-neutral-500">Colaboradora: </span><span className="font-semibold" data-testid="timesheet-name">{collab.name}</span></p>
            <p><span className="text-neutral-500">Função: </span>{collab.role}</p>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-xs" data-testid="timesheet-table">
              <thead className="bg-neutral-900 text-gold"><tr>{["Data", "Dia", "Início", "Pausa", "Retorno", "Término", "Pausa total", "Horas", "Ocorrência"].map((h) => <th key={h} className="border border-neutral-900 px-2 py-1.5 text-left font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.date} data-testid={`timesheet-row-${d.date}`} className={d.wd === 0 ? "bg-neutral-50 text-neutral-400" : ""}>
                    <Cell>{String(d.day).padStart(2, "0")}/{month.slice(5)}</Cell>
                    <Cell>{WD[d.wd]}</Cell>
                    <Cell className={d.note === "Atraso" ? "font-semibold text-red-600" : ""}>{d.start || ""}</Cell>
                    <Cell>{timeOf(d.entry, "pausa") || ""}</Cell>
                    <Cell>{timeOf(d.entry, "retorno") || ""}</Cell>
                    <Cell>{timeOf(d.entry, "termino") || ""}</Cell>
                    <Cell>{d.entry ? fmtMin(paused(d.entry)) : ""}</Cell>
                    <Cell className="font-semibold">{d.entry ? fmtMin(worked(d.entry)) : ""}</Cell>
                    <Cell className={d.abs?.type === "falta" ? "font-semibold text-red-600" : ""}>{d.future ? "" : d.note}</Cell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Totals days={days} />
          <p className="mt-6 text-xs text-neutral-500">Declaro que os registros acima correspondem à minha jornada de trabalho no período.</p>
          <div className="mt-14 flex flex-wrap justify-around gap-10">
            <Sign label="Colaboradora" name={collab.name} />
            <Sign label={`Empresária · ${company.name}`} name={company.owner} />
          </div>
        </div>
      )}
    </div>
  );
}
